// src/hooks/useLogo.js
import { useState, useEffect } from 'react';

/**
 * Custom hook to get the application logo from settings
 * Falls back to default branding if logo is not available
 * @param {string} userType - Optional user type to determine which logo to fetch
 * @param {number} orgId - Optional organization ID for fetching org-specific logo
 */
export const useLogo = (userType = null, orgId = null) => {
    const [logo, setLogo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const token = localStorage.getItem('haazri_token');
                if (!token) {
                    setLoading(false);
                    return;
                }

                // For client_admin and app_admin, fetch organization logo
                if ((userType === 'client_admin' || userType === 'app_admin') && orgId) {
                    const cacheKey = `org_logo_${orgId}`;
                    const cachedLogo = localStorage.getItem(cacheKey);

                    if (cachedLogo && cachedLogo !== 'null' && cachedLogo !== 'undefined') {
                        setLogo(cachedLogo);
                        setLoading(false);
                    }

                    // Fetch organization details
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/organizations/${orgId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const result = await response.json();
                        const data = result.data || result;
                        const organization = data.organization || data;

                        if (organization.logo) {
                            const logoUrl = organization.logo.startsWith('http')
                                ? organization.logo
                                : `${process.env.REACT_APP_API_URL}/${organization.logo}`;

                            setLogo(logoUrl);
                            localStorage.setItem(cacheKey, logoUrl);
                        } else {
                            // No org logo, fall back to system logo
                            await fetchSystemLogo();
                        }
                    } else {
                        // Failed to fetch org logo, fall back to system logo
                        await fetchSystemLogo();
                    }
                } else {
                    // For core_admin and unauthenticated users, fetch system logo
                    await fetchSystemLogo();
                }
            } catch (error) {
                console.error('Error fetching logo:', error);
                // On error, try to fetch system logo as fallback
                try {
                    await fetchSystemLogo();
                } catch (fallbackError) {
                    console.error('Error fetching system logo:', fallbackError);
                }
            } finally {
                setLoading(false);
            }
        };

        const fetchSystemLogo = async () => {
            // Try to get system logo from localStorage cache first
            const cachedLogo = localStorage.getItem('app_logo');
            if (cachedLogo && cachedLogo !== 'null' && cachedLogo !== 'undefined') {
                setLogo(cachedLogo);
                return;
            }

            const token = localStorage.getItem('haazri_token');
            if (!token) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/system`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                // API returns { status, message, data: { settings, grouped } }
                const data = result.data || result;
                const logoSetting = data.settings?.find(s => s.setting_key === 'logo');

                if (logoSetting && logoSetting.setting_value) {
                    const logoUrl = logoSetting.setting_value.startsWith('http')
                        ? logoSetting.setting_value
                        : `${process.env.REACT_APP_API_URL}${logoSetting.setting_value}`;

                    setLogo(logoUrl);
                    localStorage.setItem('app_logo', logoUrl);
                } else {
                    // No logo found, clear cache
                    setLogo(null);
                    localStorage.removeItem('app_logo');
                }
            }
        };

        fetchLogo();
    }, [userType, orgId]);

    return { logo, loading };
};

/**
 * Clear the cached logo (useful after uploading a new logo)
 * @param {number} orgId - Optional organization ID to clear org-specific cache
 */
export const clearLogoCache = (orgId = null) => {
    localStorage.removeItem('app_logo');
    if (orgId) {
        localStorage.removeItem(`org_logo_${orgId}`);
    }
};

/**
 * Component to render the logo with fallback to text branding
 * For unauthenticated pages (auth pages), fetches public logo without token
 */
export const Logo = ({ className = '', size = 'medium', usePublicLogo = false }) => {
    const [publicLogo, setPublicLogo] = useState(null);
    const [publicLoading, setPublicLoading] = useState(usePublicLogo);
    const { logo, loading } = useLogo();

    // Fetch public logo for unauthenticated pages
    useEffect(() => {
        if (!usePublicLogo) return;

        const fetchPublicLogo = async () => {
            try {
                // Check cache first
                const cachedLogo = localStorage.getItem('public_app_logo');
                if (cachedLogo && cachedLogo !== 'null' && cachedLogo !== 'undefined') {
                    setPublicLogo(cachedLogo);
                    setPublicLoading(false);
                    return;
                }

                // Fetch from public endpoint (no auth required)
                const response = await fetch(`${process.env.REACT_APP_API_URL}/public/logo`);

                if (response.ok) {
                    const result = await response.json();
                    const logoUrl = result.logo || result.data?.logo;

                    if (logoUrl) {
                        const fullLogoUrl = logoUrl.startsWith('http')
                            ? logoUrl
                            : `${process.env.REACT_APP_API_URL}${logoUrl}`;

                        setPublicLogo(fullLogoUrl);
                        localStorage.setItem('public_app_logo', fullLogoUrl);
                    }
                }
            } catch (error) {
                console.error('Error fetching public logo:', error);
            } finally {
                setPublicLoading(false);
            }
        };

        fetchPublicLogo();
    }, [usePublicLogo]);

    const sizeClasses = {
        small: { container: 'logo-small', icon: '30px', text: '1.2rem' },
        medium: { container: 'logo-medium', icon: '40px', text: '1.5rem' },
        large: { container: 'logo-large', icon: '50px', text: '2rem' }
    };

    const currentSize = sizeClasses[size] || sizeClasses.medium;

    // Use public logo if specified
    const displayLogo = usePublicLogo ? publicLogo : logo;
    const isLoading = usePublicLogo ? publicLoading : loading;

    if (isLoading) {
        return (
            <div className={`logo ${currentSize.container} ${className}`}>
                <div className="logo-icon" style={{ width: currentSize.icon, height: currentSize.icon }}>
                    H
                </div>
                <span className="logo-text" style={{ fontSize: currentSize.text }}>
                    eHAAZRI
                </span>
            </div>
        );
    }

    if (displayLogo) {
        return (
            <div className={`logo logo-image ${currentSize.container} ${className}`}>
                <img
                    src={displayLogo}
                    alt="eHAAZRI Logo"
                    className="logo-img"
                    style={{ maxHeight: currentSize.icon, maxWidth: '200px', objectFit: 'contain' }}
                />
            </div>
        );
    }

    // Fallback to text logo
    return (
        <div className={`logo ${currentSize.container} ${className}`}>
            <div className="logo-icon" style={{ width: currentSize.icon, height: currentSize.icon }}>
                H
            </div>
            <span className="logo-text" style={{ fontSize: currentSize.text }}>
                eHAAZRI
            </span>
        </div>
    );
};

export default useLogo;
